/**
 * Faq content — the editorial copy for `/faq`.
 *
 * HONESTY GUARDRAILS (pinned by upstream spec):
 *   - Stripe identity verification, live payouts, and reviews are NOT
 *     yet shipped. Anywhere this copy references them it MUST use
 *     "at launch" / "rolling out" / "coming with our first
 *     neighborhoods" / "first wave" framing.
 *   - DO NOT claim live background checks. Use "business and
 *     identity onboarding" + "rolling out" wording.
 *   - When in doubt → UNDERSTATE.
 *
 * BRAND CAPS (pinned):
 *   - `Pluck`, `Pluckers`, `Plucking`, `PooPartner`, `PooPluckers`
 *     are always capitalized.
 *   - The domain: `PooPluckers.com` in display copy (title case is
 *     the brand rule per 2026-07-20 dispatch — lowercase misparses
 *     as "poop-luckers"). `poopluckers.com` (lowercase) stays in
 *     URLs / hrefs / mailto: contexts only. Same split applies to
 *     `instagram.com/poopluckers/` — display "Instagram" but URL
 *     stays lowercase because the platform handle is case-sensitive.
 *
 * PARTNER SECTION VOICE (2026-07-21 dispatch, B2B alignment):
 *   - PooPartners are ESTABLISHED LOCAL BUSINESSES, not individual
 *     giggers. Voice: peer-to-business, not gig-recruiter.
 *   - NEVER surface a commission percentage — dollar-outcomes only
 *     ("$16+ per Pluck", "$20 per visit"). No `%` character in
 *     partner-facing pricing copy. A drift-guard test in
 *     `pages/FAQ.partnerCopy.test.jsx` fails CI on regression.
 *   - NEVER promise a bookings go-live date ("soon", "this
 *     quarter", "spring"). Metro-by-metro rollout language only.
 *   - NEVER claim a live background check. The certification
 *     program is future-facing ("rolling out with our certification
 *     program"). Business + identity verification IS live (EIN + W-9
 *     encrypted at intake, Stripe identity check at Connect
 *     onboarding) — say only that.
 *
 * Dollar figures are canonical against `memory/TIER_PAGE_SPEC.md`:
 *   - $20 per visit = 1-dog weekly baseline (customer-facing price)
 *   - $16+ per Pluck = Standard-tier partner keep
 *   - Standard / Preferred / Elite = the three tier names
 *
 * Location neutrality: copy must read country-wide. NEVER reference
 * "Omaha" — use "in your area" / "your neighborhood".
 */

export const FAQ_HERO = {
  kick: "Help & answers",
  headline: "Frequently asked questions",
  sub:
    "Quick answers to the questions PooPluckers' first wave of customers and " +
    "PooPartners ask the most. Two sides of the same marketplace — pick yours.",
};

export const FAQ_CUSTOMER = {
  groupLabel: "For dog owners",
  groupKick: "Customers",
  items: [
    {
      q: "How does PooPluckers work?",
      a:
        "PooPluckers is a marketplace. You tell us about your yard and your " +
        "pack, and we match you with a verified local PooPartner who picks up " +
        "the waste on your schedule. You set the cadence, your PooPartner shows " +
        "up, and your yard stays spotless. No clipboard, no door-to-door, no " +
        "guesswork.",
    },
    {
      q: "How much does it cost?",
      a:
        "Per-dog pricing starting at $20 for the first dog, with a small " +
        "add-on for each additional pup. You pay your PooPartner's listed " +
        "rate — no signup fee, no contracts. See the Pricing page for the " +
        "full breakdown.",
    },
    {
      q: "What areas do you serve?",
      a:
        "We're launching neighborhood by neighborhood. The Map page shows the " +
        "ZIPs in your area where verified PooPartners are active right now. " +
        "If your ZIP isn't covered yet, drop your email on the waitlist — " +
        "we'll let you know the moment we launch nearby.",
    },
    {
      q: "Do I need to be home during the cleanup?",
      a:
        "Nope. As long as your PooPartner can access the yard (unlocked gate, " +
        "lockbox code, or whatever you set up during booking) and your dogs " +
        "are secured, you don't have to be there. Most customers schedule " +
        "visits while they're at work.",
    },
    {
      q: "What about my dog during the visit?",
      a:
        "Dogs should be kept inside or kenneled during the cleanup — so they " +
        "can't slip through a gate while your PooPartner is working. If you " +
        "can't fully secure them, a small per-dog securement fee covers the " +
        "extra time and care your PooPartner takes.",
    },
    {
      q: "How do I pay?",
      a:
        "Securely online — credit or debit, no cash exchange. Payment runs " +
        "through Stripe at launch, with funds handled inside the platform so " +
        "your PooPartner is paid promptly and you have a clean digital " +
        "receipt for every visit.",
    },
    {
      q: "What if the job isn't done right?",
      a:
        "Tell us. We'll make it right. Our PooPartner satisfaction approach " +
        "is rolling out alongside our first neighborhoods — when something " +
        "goes sideways, we work directly with you and your PooPartner to fix " +
        "it. Honest service is the whole point.",
    },
    {
      q: "Can I cancel anytime?",
      a:
        "Yes. No contracts, no cancellation fees, no awkward goodbye email. " +
        "Pause for a season, switch PooPartners, or stop entirely — your " +
        "schedule, your call.",
    },
    {
      // 2026-07-21 dispatch: rewrite this answer honestly, present-tense
      // only. Business + identity verification IS live; individual
      // background checks are future-facing ("rolling out with our
      // certification program") — never claimed as currently live.
      q: "Are PooPartners background-checked and verified?",
      a:
        "Every PooPartner is business-verified. During application we collect " +
        "and encrypt EIN and W-9 details, and identity is confirmed through " +
        "Stripe when a partner onboards for payouts. Individual background " +
        "checks are rolling out with our certification program — we'll never " +
        "claim a check is live before it actually is.",
    },
  ],
};

export const FAQ_PARTNER = {
  groupLabel: "For PooPartners",
  groupKick: "Become a PooPartner",
  items: [
    {
      // Voice: peer-to-business, not gig-recruiter. PooPartners are
      // ESTABLISHED LOCAL BUSINESSES adding PooPluckers as a channel.
      q: "What is a PooPartner?",
      a:
        "An established local business — a dog-waste removal operator, lawn " +
        "or landscaping pro, or pet-service outfit — that adds PooPluckers " +
        "as a demand channel. We're not a gig platform. We recruit real " +
        "businesses, not individual giggers, so your customers get " +
        "consistent, professional service and you get demand routed to your " +
        "existing operation.",
    },
    {
      // 2026-07-21 dispatch, new item. Address the top B2B objection
      // up front: does the platform compete with the operator's own book?
      q: "Will PooPluckers compete with my business?",
      a:
        "No. Your book stays entirely yours. We don't route your existing " +
        "customers away, and joining PooPluckers doesn't affect any work " +
        "you already have — we're an additional demand channel, not a " +
        "replacement.",
    },
    {
      // 2026-07-21 dispatch, replaces the old "Do I set my own prices
      // and schedule?" Q. Two clear answers instead of one blurry one.
      // Drift-guard: this answer MUST contain "$20 per visit" and MUST
      // contain zero `%` characters. See FAQ.partnerCopy.test.jsx.
      q: "Who sets the price on platform bookings?",
      a:
        "We do — starting at $20 per visit, deliberately premium to going " +
        "rates. That premium is what makes your keep beat typical solo " +
        "economics. Your own direct business and its pricing stay entirely " +
        "yours.",
    },
    {
      // 2026-07-21 dispatch, replaces the second half of the old
      // "Do I set my own prices and schedule?" Q.
      q: "Do I choose which jobs I take?",
      a:
        "Yes — take the jobs that fit your routes; skip the ones that " +
        "don't. No exclusivity, leave anytime.",
    },
    {
      // 2026-07-21 dispatch: dollar-outcomes voice; NO commission
      // percentage anywhere. Drift-guard: this answer MUST contain
      // "$16+" and MUST contain zero `%` characters.
      q: "How do I get paid?",
      a:
        "Stripe payout, direct to your bank — live payment rails come " +
        "online with our first wave. Your keep starts at $16+ per Pluck " +
        "on the Standard tier: clear dollar outcomes per visit, no " +
        "guessing games. Preferred and Elite unlock higher keeps as your " +
        "business grows.",
    },
    {
      // 2026-07-21 dispatch: free-to-apply framing; Founding = 90
      // days of Preferred tier free (spec-provided language). No `%`.
      q: "What does it cost to join?",
      a:
        "Free to apply. Standard tier is $0 — you keep more than going " +
        "solo. Founding PooPartners get 90 days of Preferred tier free. " +
        "The full fee schedule is shown at signup so you always know what " +
        "you're agreeing to.",
    },
    {
      // 2026-07-21 dispatch: reinforce platform-does-demand-generation
      // language — the operator doesn't chase leads, we route them.
      q: "How do I get customers?",
      a:
        "We handle marketing, booking, and billing. Jobs route to you " +
        "based on the ZIPs you declare as your service area — you don't " +
        "chase leads, we route demand to your inbox. Build a strong " +
        "reputation and customers come back on a recurring schedule.",
    },
    {
      q: "What areas can I serve?",
      a:
        "You pick the ZIPs. Add neighborhoods you can comfortably reach, " +
        "skip the ones you can't. Update your coverage whenever your " +
        "capacity changes — no lock-in.",
    },
    {
      // 2026-07-21 dispatch, new item. Mirrors what
      // `backend/poopartner_intake.py` actually collects (business
      // details + EIN plaintext → envelope-encrypted + W-9 upload
      // encrypted at rest). Does NOT mention insurance (not
      // collected by the intake flow).
      q: "What does the application involve?",
      a:
        "Business details, EIN, and a W-9 — both EIN and W-9 are stored " +
        "encrypted. Reviewed within 2 business days.",
    },
    {
      // 2026-07-21 dispatch, new item. NO DATES, NO "soon", NO ETA
      // — metro-by-metro rollout language only.
      q: "When do customer bookings start?",
      a:
        "We're signing partners first, on purpose — bookings open metro " +
        "by metro, and Founding PooPartners are first in line when demand " +
        "routes to their area.",
    },
  ],
};

export const FAQ_CTA = {
  headline: "Still curious?",
  sub:
    "We're launching neighborhood by neighborhood \u2014 join the waitlist " +
    "to be first in line when booking opens in your area, or apply to add " +
    "PooPluckers to your business.",
  customer_label: "Join the waitlist",
  customer_to: "/#waitlist",
  partner_label: "Become a PooPartner",
  partner_to: "/signup?role=provider",
};
